import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { KpiInfo } from '../../../components/delivery/widgets/KpiTile';
import { useProgramFinancialSummary } from '../../../components/delivery/widgets/useProgramFinancialSummary';
import { FINANCIAL_HEALTH_LABELS, FINANCIAL_HEALTH_TIPS } from '../../../constants/deliveryMoney';
import { ActionGate } from '../../../lib/access/ActionGate';
import { MaskedField } from '../../../lib/access/MaskedField';
import { programRef } from '../../../lib/access/types';
import { useResolveAction } from '../../../lib/access/useResolveAction';
import { useView } from '../../../lib/access/useView';
import { formatUtcYmd } from '../_shared/api/nullable';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ErrorState } from '../_shared/components/PageStates';
import { FinancialHistoryChart } from './FinancialHistoryChart';
import { useProgramFinancialMonths, useSetFundingAlertThreshold, useUpsertProgramFinancialMonth } from './hooks';
import { centsToDollarString, dollarsToCents } from './money';
const fmt = (cents)=>`$${centsToDollarString(cents)}`;
// The "area:financials.eac" capability is the RPC-side gate for both writes
// exposed here (UpsertProgramFinancialMonth, SetProgramFundingAlertThreshold —
// see financial_health.go). One constant so the two ActionGate call sites and
// the form's readOnly wiring can't drift onto different action strings.
const WRITE_ACTION = 'area:financials.eac';
const monthDefaults = (m)=>({
        periodMonth: m?.periodMonth ?? '',
        burnRateDollars: m ? centsToDollarString(m.burnRateCents) : '0.00',
        arBalanceDollars: m ? centsToDollarString(m.arBalanceCents) : '0.00',
        comment: m?.comment ?? ''
    });
const monthFields = [
    {
        kind: 'month',
        name: 'periodMonth',
        label: 'Month',
        required: true,
        testId: Testids.deliveryFinancial.healthMonthInput
    },
    {
        kind: 'text',
        name: 'burnRateDollars',
        label: 'Monthly burn rate ($)'
    },
    {
        kind: 'text',
        name: 'arBalanceDollars',
        label: 'Accounts receivable balance ($)'
    },
    {
        kind: 'textarea',
        name: 'comment',
        label: 'Comment',
        rows: 2
    }
];
const historyColumns = [
    {
        key: 'periodMonth',
        header: 'Month'
    },
    {
        key: 'burnRateCents',
        header: 'Burn rate',
        render: (row)=>fmt(row.burnRateCents),
        maskAs: 'burn_rate_cents'
    },
    {
        key: 'arBalanceCents',
        header: 'AR balance',
        render: (row)=>fmt(row.arBalanceCents),
        maskAs: 'ar_balance_cents'
    },
    {
        key: 'comment',
        header: 'Comment'
    },
    {
        key: 'recordedBy',
        header: 'Recorded by'
    }
];
// Truthful PoP remaining statement per popState — see selectProgramPoPWindow
// in financial_health.go for how the window/state is chosen server-side.
function popRemainingStatement(health) {
    if (!health) return 'No period dates recorded';
    switch(health.popState){
        case 'ACTIVE':
            return `Ends ${formatUtcYmd(health.popEnd) ?? '—'} · ${health.popDaysRemaining} days remaining`;
        case 'NOT_STARTED':
            return `Starts ${formatUtcYmd(health.popStart) ?? '—'}`;
        case 'COMPLETED':
            return `Period ended ${formatUtcYmd(health.popEnd) ?? '—'}`;
        default:
            return 'No period dates recorded';
    }
}
export function FinancialHealthTab({ programId }) {
    const ref = programRef(programId);
    const { allowed: canWrite } = useResolveAction(ref, WRITE_ACTION);
    const view = useView(ref);
    const fundingConsumedPctMasked = view.fieldMasked('funding_consumed_pct');
    const burnRateMasked = view.fieldMasked('burn_rate_cents');
    const arBalanceMasked = view.fieldMasked('ar_balance_cents');
    const { data: summary, isLoading: summaryLoading, isError: summaryError } = useProgramFinancialSummary(programId);
    const health = summary?.financialHealth;
    const { data: monthsData, isLoading: monthsLoading } = useProgramFinancialMonths(programId);
    const months = monthsData?.items ?? [];
    const upsert = useUpsertProgramFinancialMonth();
    const setThreshold = useSetFundingAlertThreshold();
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [thresholdDraft, setThresholdDraft] = useState(null);
    const thresholdValue = thresholdDraft ?? (health ? String(health.thresholdPct) : '');
    if (summaryError) {
        return /*#__PURE__*/ _jsx(ErrorState, {
            title: "Couldn't load financial health"
        });
    }
    // dollarsToCents throws on non-numeric/negative/>2-decimal input (by
    // design — see money.ts). Parse both money fields BEFORE calling
    // mutateAsync so a typo (e.g. "abc", "1.2.3", "-5", "1.005") surfaces as a
    // named, readable inline error instead of a raw exception string, and
    // never reaches the mutation. EntityForm's submit handler already catches
    // a thrown Error here, renders it in the dialog's inline error banner, and
    // — since we throw before setFormOpen(false) runs — leaves the dialog open
    // with the user's typed values intact.
    const parseMoneyField = (label, raw)=>{
        try {
            return dollarsToCents(raw);
        } catch  {
            throw new Error(`${label} must be a non-negative dollar amount with up to 2 decimal places (got "${raw.trim()}")`);
        }
    };
    const submitMonth = async (values)=>{
        const burnRateCents = parseMoneyField('Monthly burn rate ($)', values.burnRateDollars);
        const arBalanceCents = parseMoneyField('Accounts receivable balance ($)', values.arBalanceDollars);
        await upsert.mutateAsync({
            programId,
            periodMonth: values.periodMonth,
            burnRateCents,
            arBalanceCents,
            comment: values.comment ?? ''
        });
        setFormOpen(false);
    };
    const handleSaveThreshold = async ()=>{
        const pct = Number(thresholdValue);
        if (!Number.isFinite(pct) || pct < 1 || pct > 100) return;
        await setThreshold.mutateAsync({
            programId,
            thresholdPct: pct
        });
        setThresholdDraft(null);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        "data-testid": Testids.deliveryFinancial.healthPanel,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "grid gap-3 sm:grid-cols-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rounded-lg border bg-card p-4",
                        "data-testid": Testids.deliveryFinancial.healthCurrentBurn,
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs font-medium uppercase tracking-wide text-muted-foreground",
                                children: FINANCIAL_HEALTH_LABELS.monthlyBurnRate
                            }),
                            summaryLoading ? /*#__PURE__*/ _jsx("p", {
                                className: "mt-1 text-sm text-muted-foreground",
                                children: "—"
                            }) : health?.hasCurrent ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-1 text-2xl font-semibold tabular-nums",
                                        children: /*#__PURE__*/ _jsx(MaskedField, {
                                            fieldName: "burn_rate_cents",
                                            resource: ref,
                                            children: fmt(health.currentBurnRateCents)
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "mt-1 text-xs text-muted-foreground",
                                        children: health.currentPeriodMonth
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx("p", {
                                className: "mt-1 text-sm text-muted-foreground",
                                children: "No month recorded yet"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rounded-lg border bg-card p-4",
                        "data-testid": Testids.deliveryFinancial.healthCurrentAr,
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs font-medium uppercase tracking-wide text-muted-foreground",
                                children: FINANCIAL_HEALTH_LABELS.accountsReceivable
                            }),
                            summaryLoading ? /*#__PURE__*/ _jsx("p", {
                                className: "mt-1 text-sm text-muted-foreground",
                                children: "—"
                            }) : health?.hasCurrent ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-1 text-2xl font-semibold tabular-nums",
                                        children: /*#__PURE__*/ _jsx(MaskedField, {
                                            fieldName: "ar_balance_cents",
                                            resource: ref,
                                            children: fmt(health.currentArBalanceCents)
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "mt-1 text-xs text-muted-foreground",
                                        children: health.currentPeriodMonth
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx("p", {
                                className: "mt-1 text-sm text-muted-foreground",
                                children: "No month recorded yet"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rounded-lg border bg-card p-4 space-y-3",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs font-medium uppercase tracking-wide text-muted-foreground",
                        children: "Funding progress"
                    }),
                    summaryLoading ? /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "—"
                    }) : health?.hasFunding ? /*#__PURE__*/ _jsxs("div", {
                        className: "grid gap-3 text-sm sm:grid-cols-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.deliveryFinancial.healthFundingAvailable,
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-muted-foreground",
                                        children: FINANCIAL_HEALTH_LABELS.fundingAvailable
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-lg font-semibold tabular-nums",
                                        children: /*#__PURE__*/ _jsx(MaskedField, {
                                            fieldName: "total_available_funding_cents",
                                            resource: ref,
                                            children: fmt(health.totalAvailableFundingCents)
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.deliveryFinancial.healthFundingConsumed,
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-muted-foreground",
                                        children: FINANCIAL_HEALTH_LABELS.fundingConsumed
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-lg font-semibold tabular-nums",
                                        children: /*#__PURE__*/ _jsx(MaskedField, {
                                            fieldName: "funding_consumed_cents",
                                            resource: ref,
                                            children: fmt(health.fundingConsumedCents)
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.deliveryFinancial.healthFundingRemaining,
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-muted-foreground",
                                        children: FINANCIAL_HEALTH_LABELS.fundingRemaining
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-lg font-semibold tabular-nums",
                                        children: /*#__PURE__*/ _jsx(MaskedField, {
                                            fieldName: "funding_remaining_cents",
                                            resource: ref,
                                            children: health.fundingRemainingCents < BigInt(0) ? `Over funding by ${fmt(-health.fundingRemainingCents)}` : fmt(health.fundingRemainingCents)
                                        })
                                    })
                                ]
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No funding recorded on this program yet"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid gap-4 pt-1 sm:grid-cols-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.deliveryFinancial.healthConsumptionBar,
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-baseline justify-between text-xs text-muted-foreground",
                                        children: [
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "inline-flex items-center gap-1 uppercase tracking-wide",
                                                children: [
                                                    FINANCIAL_HEALTH_LABELS.fundingConsumed,
                                                    /*#__PURE__*/ _jsx(KpiInfo, {
                                                        label: FINANCIAL_HEALTH_LABELS.fundingConsumed,
                                                        tip: FINANCIAL_HEALTH_TIPS.fundingConsumedForThreshold(health?.thresholdPct ?? 75),
                                                        testid: Testids.deliveryShared.infoTip,
                                                        dataName: "fundingConsumedPct"
                                                    })
                                                ]
                                            }),
                                            health?.hasFunding ? /*#__PURE__*/ _jsx(MaskedField, {
                                                fieldName: "funding_consumed_pct",
                                                resource: ref,
                                                children: /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        health.fundingConsumedPct.toFixed(0),
                                                        "%"
                                                    ]
                                                })
                                            }) : /*#__PURE__*/ _jsx("span", {
                                                children: "—"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted",
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: `h-full rounded-full transition-[width] ${fundingConsumedPctMasked ? 'bg-muted-foreground/30' : health?.thresholdBreached ? 'bg-danger' : 'bg-success'}`,
                                            style: {
                                                width: fundingConsumedPctMasked ? '0%' : `${Math.max(0, Math.min(100, health?.fundingConsumedPct ?? 0))}%`
                                            }
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.deliveryFinancial.healthPopBar,
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-baseline justify-between text-xs text-muted-foreground",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "uppercase tracking-wide",
                                                children: "Period elapsed"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                children: health && health.popState !== 'UNKNOWN' ? `${health.popElapsedPct.toFixed(0)}%` : '—'
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted",
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: "h-full rounded-full bg-info transition-[width]",
                                            style: {
                                                width: `${health && health.popState !== 'UNKNOWN' ? Math.max(0, Math.min(100, health.popElapsedPct)) : 0}%`
                                            }
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rounded-lg border bg-card p-4",
                "data-testid": Testids.deliveryFinancial.healthPopRemaining,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs font-medium uppercase tracking-wide text-muted-foreground",
                        children: FINANCIAL_HEALTH_LABELS.periodOfPerformance
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "mt-1 text-sm",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: health?.popLabel || 'Period of performance'
                            }),
                            ' — ',
                            summaryLoading ? '—' : popRemainingStatement(health)
                        ]
                    })
                ]
            }),
            !summaryLoading && health?.thresholdBreached && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                className: "rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger",
                "data-testid": Testids.deliveryFinancial.healthThresholdWarning,
                children: [
                    "Funding consumption has reached",
                    ' ',
                    /*#__PURE__*/ _jsx(MaskedField, {
                        fieldName: "funding_consumed_pct",
                        resource: ref,
                        children: /*#__PURE__*/ _jsxs("span", {
                            className: "font-semibold",
                            children: [
                                health.fundingConsumedPct.toFixed(0),
                                "%"
                            ]
                        })
                    }),
                    ' ',
                    "— at or above the ",
                    health.thresholdPct,
                    "% alert threshold."
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-end gap-3 rounded-lg border bg-card p-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                htmlFor: "funding-alert-threshold",
                                className: "text-xs font-medium uppercase tracking-wide text-muted-foreground",
                                children: FINANCIAL_HEALTH_LABELS.fundingAlertThreshold
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "funding-alert-threshold",
                                type: "number",
                                min: 1,
                                max: 100,
                                disabled: !canWrite,
                                value: thresholdValue,
                                onChange: (e)=>setThresholdDraft(e.target.value),
                                "data-testid": Testids.deliveryFinancial.healthThresholdInput,
                                className: "mt-1 w-24"
                            }),
                            health?.thresholdIsDefault && /*#__PURE__*/ _jsxs("p", {
                                className: "mt-1 text-xs text-muted-foreground",
                                children: [
                                    "Default ",
                                    health.thresholdPct,
                                    "%"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ActionGate, {
                        action: WRITE_ACTION,
                        resource: ref,
                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: handleSaveThreshold,
                            disabled: setThreshold.isPending,
                            "data-testid": Testids.deliveryFinancial.healthThresholdSave,
                            children: "Save"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(ActionGate, {
                    action: WRITE_ACTION,
                    resource: ref,
                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>{
                            setEditing(null);
                            setFormOpen(true);
                        },
                        "data-testid": Testids.deliveryFinancial.healthRecordMonthButton,
                        children: "Record month"
                    })
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: months,
                columns: historyColumns,
                testid: Testids.deliveryFinancial.healthHistoryTable,
                rowTestid: Testids.deliveryFinancial.healthHistoryRow,
                rowKey: (m)=>m.id,
                isLoading: monthsLoading,
                onRowClick: (row)=>{
                    setEditing(row);
                    setFormOpen(true);
                },
                resource: ref
            }),
            /*#__PURE__*/ _jsx(FinancialHistoryChart, {
                months: months,
                burnRateMasked: burnRateMasked,
                arBalanceMasked: arBalanceMasked,
                resource: ref
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: editing ? `Edit ${editing.periodMonth}` : 'Record Month',
                fields: monthFields,
                defaultValues: monthDefaults(editing),
                onSubmit: submitMonth,
                isSubmitting: upsert.isPending,
                readOnly: !canWrite
            })
        ]
    });
}
